/**
 * Amenity icon set + keyword resolver — widget-side port of the HomeRunner
 * Toolkit's `amenity-icons` module (`includes/AmenityIcons/Icons.php` and
 * `includes/AmenityIcons/Resolver.php`, homerunner-toolkit repo). Dashboard
 * issue #1423 (widget parity).
 *
 * Kept a PURE, dependency-free port on purpose:
 *  - Same rule table, same rule order, same word-boundary anchoring as the
 *    toolkit, so a WordPress site running the toolkit module and a HomeRunner
 *    platform widget rendering the same amenity name pick the same icon.
 *  - Icon paths are copied verbatim from the toolkit's static Lucide subset
 *    rather than reused via the Iconify search API the chip-icon picker uses
 *    (dashboard#871 / PR #1415). That picker does a live browser fetch to
 *    api.iconify.design, which PR #1415 itself flagged as needing human
 *    review because it adds an outbound network dependency; a customer-facing
 *    property page rendering amenity icons on every load should not depend on
 *    a third-party API being up, and inlining keeps this change inside the
 *    self-merge grant (no new external call, no new dependency).
 *
 * No WordPress calls here — this only knows about a name string and returns
 * an icon id, mirroring `Resolver::match_name()`.
 */

/**
 * Icon id -> inline SVG path/shape markup (viewBox 0 0 24 24, no <svg> wrapper).
 * Verbatim copy of `HomerunnerToolkit\AmenityIcons\Icons::PATHS`.
 *
 * Icon paths are from Lucide (https://lucide.dev), distributed under the ISC
 * License, reproduced here as that license requires:
 *
 *   ISC License
 *
 *   Copyright (c) for portions of Lucide are held by Cole Bemis 2013-2022 as
 *   part of Feather (MIT). All other copyright (c) for Lucide are held by
 *   Lucide Contributors 2022.
 *
 *   Permission to use, copy, modify, and/or distribute this software for any
 *   purpose with or without fee is hereby granted, provided that the above
 *   copyright notice and this permission notice appear in all copies.
 *
 *   THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 *   WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 *   MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 *   ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 *   WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 *   ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 *   OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 */
export const AMENITY_ICON_PATHS = {
  wifi: '<path d="M12 20h.01"/><path d="M8.5 16.4a5 5 0 0 1 7 0"/><path d="M5 12.9a10 10 0 0 1 14 0"/><path d="M1.5 9.4a15 15 0 0 1 21 0"/>',
  tv: '<rect width="20" height="15" x="2" y="7" rx="2"/><polyline points="17 2 12 7 7 2"/>',
  washer: '<rect width="18" height="20" x="3" y="2" rx="2"/><circle cx="12" cy="13" r="5"/><path d="M12 18a2.5 2.5 0 0 0 0-5 2.5 2.5 0 0 1 0-5"/><path d="M6 6h.01"/><path d="M10 6h.01"/>',
  snow: '<line x1="2" x2="22" y1="12" y2="12"/><line x1="12" x2="12" y1="2" y2="22"/><path d="m20 16-4-4 4-4"/><path d="m4 8 4 4-4 4"/><path d="m16 4-4 4-4-4"/><path d="m8 20 4-4 4 4"/>',
  pool: '<path d="M19 5a2 2 0 0 0-2 2v11"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M7 13h10"/><path d="M7 9h10"/><path d="M9 5a2 2 0 0 0-2 2v11"/>',
  waves: '<path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>',
  flame: '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>',
  car: '<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/>',
  coffee: '<path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/><line x1="6" x2="6" y1="2" y2="4"/><line x1="10" x2="10" y1="2" y2="4"/><line x1="14" x2="14" y1="2" y2="4"/>',
  fan: '<path d="M10.827 16.379a6.082 6.082 0 0 1-8.618-7.002l5.412 1.45a6.082 6.082 0 0 1 7.002-8.618l-1.45 5.412a6.082 6.082 0 0 1 8.618 7.002l-5.412-1.45a6.082 6.082 0 0 1-7.002 8.618l1.45-5.412Z"/><path d="M12 12v.01"/>',
  utensils: '<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>',
  stove: '<path d="M2 12h20"/><path d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8"/><path d="m4 8 16-4"/><path d="m8.86 6.78-.45-1.81a2 2 0 0 1 1.45-2.43l1.94-.48a2 2 0 0 1 2.43 1.46l.45 1.8"/>',
  fridge: '<path d="M5 6a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6Z"/><path d="M5 10h14"/><path d="M15 7v6"/>',
  sun: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>',
  dumbbell: '<path d="M14.4 14.4 9.6 9.6"/><path d="M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.767 1.768a2 2 0 1 1-2.829-2.829l6.364-6.364a2 2 0 1 1 2.829 2.829l-1.768 1.767a2 2 0 1 1 2.828 2.829z"/><path d="m21.5 21.5-1.4-1.4"/><path d="M3.9 3.9 2.5 2.5"/><path d="M6.404 12.768a2 2 0 1 1-2.829-2.829l1.768-1.767a2 2 0 1 1-2.828-2.829l2.828-2.828a2 2 0 1 1 2.829 2.828l1.767-1.768a2 2 0 1 1 2.829 2.829z"/>',
  shield: '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>',
  baby: '<path d="M9 12h.01"/><path d="M15 12h.01"/><path d="M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5"/><path d="M19 6.3a9 9 0 0 1 1.8 3.9 2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 2.5s-.9 2.5-2 2.5c-.8 0-1.5-.4-1.5-1"/>',
  laptop: '<path d="M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9m16 0H4m16 0 1.28 2.55a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45L4 16"/>',
  paw: '<circle cx="11" cy="4" r="2"/><circle cx="18" cy="8" r="2"/><circle cx="20" cy="16" r="2"/><path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z"/>',
  bed: '<path d="M2 4v16"/><path d="M2 8h18a2 2 0 0 1 2 2v10"/><path d="M2 17h20"/><path d="M6 8v9"/>',
  bath: '<path d="M10 4 8 6"/><path d="M17 19v2"/><path d="M2 12h20"/><path d="M7 19v2"/><path d="M9 5 7.621 3.621A2.121 2.121 0 0 0 4 5v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"/>',
  shirt: '<path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z"/>',
  home: '<path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>',
} as const satisfies Record<string, string>

export type AmenityIconId = keyof typeof AMENITY_ICON_PATHS

export const AMENITY_ICON_IDS = Object.keys(AMENITY_ICON_PATHS) as AmenityIconId[]

/** Human labels for the dashboard override picker. Port of `Icons::labels()`. English only — operator-facing config, not the customer-facing `translations` group. */
export const AMENITY_ICON_LABELS: Record<AmenityIconId, string> = {
  wifi: 'Wi-Fi',
  tv: 'TV',
  washer: 'Washer / dryer',
  snow: 'Air conditioning',
  pool: 'Pool',
  waves: 'Ocean / beach',
  flame: 'BBQ / fire',
  car: 'Parking',
  coffee: 'Coffee',
  fan: 'Fan',
  utensils: 'Kitchen',
  stove: 'Stove / oven',
  fridge: 'Refrigerator',
  sun: 'Patio / outdoors',
  dumbbell: 'Gym',
  shield: 'Safety',
  baby: 'Family / kids',
  laptop: 'Workspace',
  paw: 'Pets',
  bed: 'Bed / linens',
  bath: 'Bath',
  shirt: 'Closet / iron',
  home: 'Home / entrance',
}

export function isAmenityIconId(value: string): value is AmenityIconId {
  return Object.prototype.hasOwnProperty.call(AMENITY_ICON_PATHS, value)
}

/**
 * Keyword rules, first match wins. Port of `Resolver::RULES`. Each alternative
 * is anchored at a word start, so "infants" never matches "fan" and
 * "workspace" never matches "spa". Specific phrases sit before generic words.
 *
 * A `\b` inside a rule (there are no other regex escapes in this table) always
 * marks a TRAILING boundary here — every rule that needs a leading boundary
 * gets it once, for the whole table, in `COMPILED_RULES` below, exactly like
 * the PHP source's single `'\b(?:' . $rule[0] . ')'` wrap. Keep that invariant
 * if this table is ever edited: a `\b` added anywhere but the end of an
 * alternative needs `COMPILED_RULES` revisited too.
 */
const RULES: ReadonlyArray<readonly [string, AmenityIconId]> = [
  // Before the bath rule: "air conditioner" must not hit "conditioner".
  ['air ?condition|ac\\b|central air', 'snow'],
  // Before the bath rule: "kitchen essentials" must not hit "essentials".
  ['dishwasher|kitchen|cookware|dishes|silverware|utensils|toaster|blender|glasses', 'utensils'],
  ['hot tub|jacuzzi|spa\\b', 'bath'],
  ['hair dryer|towel|shampoo|conditioner|soap|shower|bathtub|hot water|essentials|toiletries', 'bath'],
  ['pool(?! (?:billiards? )?table)|swimming', 'pool'],
  ['ocean|sea\\b|beach|water view|waterfront|lake|marina|bay\\b|kayak|paddleboard|snorkel|surf', 'waves'],
  ['wireless|wi fi|wifi|internet', 'wifi'],
  ['tv\\b|television|cable|netflix|streaming|theat(?:er|re)|projector', 'tv'],
  ['washer|dryer|laundry', 'washer'],
  ['bbq|grill|fire pit|fireplace|heating|heater|heat\\b', 'flame'],
  ['parking|garage|ev charg', 'car'],
  ['coffee|espresso', 'coffee'],
  ['fans?\\b', 'fan'],
  ['refrigerator|fridge|freezer', 'fridge'],
  ['stove|oven|microwave|cooktop', 'stove'],
  ['patio|balcony|lanai|outdoor seating|deck|terrace|garden|yard|sun\\b|hammock', 'sun'],
  ['gym|fitness|workout|exercise', 'dumbbell'],
  ['smoke detector|carbon monoxide|fire extinguisher|first aid|safety|deadbolt|lock', 'shield'],
  ['family|kids?\\b|child(?:ren)?\\b|infants?\\b|cribs?\\b|high chair|toddlers?\\b|baby\\b|babysit', 'baby'],
  ['workspace|laptop|desk|office', 'laptop'],
  ['pets?\\b|dogs?\\b', 'paw'],
  ['linen|bedding|pillow|blanket|bed\\b', 'bed'],
  ['iron|hanger|closet|wardrobe', 'shirt'],
  ['private entrance|self check|keypad|elevator|entrance|long term|home', 'home'],
]

/** Unicode-aware stand-ins for PHP's `(*UCP)`-mode `\b`. JS's native `\b` never
 * gained a unicode mode (it stays ASCII `[A-Za-z0-9_]` even with the `u`
 * flag), so "spaß" would otherwise read a boundary right after "spa" (ASCII
 * `\b` treats "ß" as non-word) and wrongly match the "spa\b" rule. These use
 * `\p{L}`/`\p{N}` (any Unicode letter/number) as the word-char set instead.
 *
 * The leading one CONSUMES a non-word character instead of asserting one with a
 * lookbehind (#1581). Lookbehind only shipped in Safari 16.4, and this package
 * builds on Vite 7's default `baseline-widely-available` target, whose floor is
 * safari16 — so a lookbehind here is not a graceful degradation. It is compiled
 * at module scope by `new RegExp`, which no bundler can lower or even see, so on
 * Safari 16.0–16.3 the very first import throws `SyntaxError` before
 * `bootstrapWidget` runs and the amenities widget mounts nothing at all.
 *
 * Consuming is exactly equivalent HERE because the only consumer is
 * `pattern.test()`, which asks whether any start position matches: for every
 * position the lookbehind would accept, the consuming form matches one
 * character earlier, and for every position it would reject, the consuming form
 * has no non-word character to eat. The `^` arm covers a match at index 0
 * (unreachable via `resolveAmenityIconId`, which space-pads the haystack, but
 * kept so the pattern is correct on its own and matches the PHP source, where
 * the lookbehind succeeds at the string start).
 *
 * CONSTRAINT: because it consumes, `exec()` on these patterns no longer reports
 * the keyword's own offset — `m.index` points at the boundary character and
 * `m[0]` includes it, EXCEPT where the zero-width `^` arm fires, in which case
 * the match is exactly the keyword. So the offset is one character wider or
 * not, depending on position. Anything that needs true match offsets must
 * account for both cases, or re-derive the boundary itself. */
const LEADING_BOUNDARY = '(?:^|[^\\p{L}\\p{N}_])'
const TRAILING_BOUNDARY = '(?![\\p{L}\\p{N}_])'

const COMPILED_RULES: ReadonlyArray<readonly [RegExp, AmenityIconId]> = RULES
  .filter(([, iconId]) => isAmenityIconId(iconId))
  .map(([source, iconId]) => {
    // Every `\b` in the table above is a trailing boundary (see the RULES
    // comment); the one leading boundary for the whole alternative group is
    // added here, matching the PHP source's single outer wrap.
    const body = source.split('\\b').join(TRAILING_BOUNDARY)
    return [new RegExp(`${LEADING_BOUNDARY}(?:${body})`, 'u'), iconId] as const
  })

/** Typographic dash/minus variants folded to a plain "-", matching the PHP normalizer. */
const DASH_VARIANTS = /[‐-―−]/gu

/** Runs of whitespace, "/", "_", ".", "-" read as one space, so "Wi-Fi", "A/C", "A.C." and doubled spaces all match the plain-word rules. */
const SEPARATORS = /[\s/_.-]+/g

/** Joins spaced-out single-letter acronyms ("B B Q", "A T M") into one word before matching. */
const SPACED_ACRONYM = /\b([a-z]) (?=[a-z]\b)/g

/**
 * Keyword match on an amenity name. Pure: no network, no DOM. Port of
 * `Resolver::match_name()`.
 *
 * @returns an icon id, or '' when nothing matches
 */
export function resolveAmenityIconId(name: string): AmenityIconId | '' {
  const trimmed = name.trim()
  if (trimmed === '') {
    return ''
  }

  let haystack = trimmed.replace(DASH_VARIANTS, '-').toLowerCase()
  haystack = ` ${haystack.replace(SEPARATORS, ' ').trim()} `
  haystack = haystack.replace(SPACED_ACRONYM, '$1')

  for (const [pattern, iconId] of COMPILED_RULES) {
    if (pattern.test(haystack)) {
      return iconId
    }
  }
  return ''
}

/** What `resolveAmenityDisplayIcon` decided to render for one amenity. */
export type AmenityIconResolution =
  | { kind: 'class'; className: string }
  | { kind: 'svg'; iconId: AmenityIconId }
  | { kind: 'none' }

/** The subset of widget options the display resolver needs, kept narrow so this file never imports the widget config schema. */
export interface AmenityIconDisplayOptions {
  defaultIcon: string
  iconFallback: 'check' | 'none'
  /** Amenity name (any case/whitespace) -> icon id, "check", or "none". Unrecognized values are ignored, same as the toolkit's per-term meta ("Unknown stored values read as auto"). */
  iconOverrides: Record<string, string>
}

function lookupOverride(name: string, overrides: Record<string, string>): string | undefined {
  const key = name.trim().toLowerCase()
  const hit = Object.entries(overrides).find(([overrideName]) => overrideName.trim().toLowerCase() === key)
  return hit?.[1]
}

/**
 * Decides what to render for one amenity, in priority order:
 *  1. The per-amenity override (operator-set in the dashboard), if it names a
 *     known icon id, "check", or "none".
 *  2. The `icon_class` Central already returns for the amenity, if any —
 *     unchanged from the widget's prior behavior.
 *  3. The keyword resolver run against the amenity name.
 *  4. The widget's `iconFallback` setting: the existing Icomoon `defaultIcon`
 *     class ("check"), or nothing ("none").
 */
export function resolveAmenityDisplayIcon(
  amenity: { name: string; icon_class?: string | null },
  options: AmenityIconDisplayOptions,
): AmenityIconResolution {
  const override = lookupOverride(amenity.name, options.iconOverrides ?? {})
  if (override === 'check') {
    return { kind: 'class', className: options.defaultIcon }
  }
  if (override === 'none') {
    return { kind: 'none' }
  }
  if (override && isAmenityIconId(override)) {
    return { kind: 'svg', iconId: override }
  }

  if (amenity.icon_class) {
    return { kind: 'class', className: amenity.icon_class }
  }

  const resolvedId = resolveAmenityIconId(amenity.name)
  if (resolvedId) {
    return { kind: 'svg', iconId: resolvedId }
  }

  return options.iconFallback === 'none' ? { kind: 'none' } : { kind: 'class', className: options.defaultIcon }
}
